/**
 * Video Utilities
 * Unified API for YouTube and Vimeo video players
 * Combines URL detection and player creation
 */

import * as youtubePlayer from './youtubePlayer';
import * as vimeoPlayer from './vimeoPlayer';

/**
 * Parse video URL and extract provider, ID, and hash (for unlisted Vimeo)
 * 
 * Supported formats:
 * - YouTube: watch, embed, shorts, youtu.be
 * - Vimeo: regular, player embed, unlisted with hash
 * 
 * @param {string} rawUrl - Video URL
 * @returns {{ source: 'youtube'|'vimeo', id: string, hash?: string }|null}
 * 
 * @example
 * getVideoDetails('https://www.youtube.com/watch?v=dQw4w9WgXcQ')
 * // => { source: 'youtube', id: 'dQw4w9WgXcQ' }
 * 
 * @example
 * getVideoDetails('https://vimeo.com/1143024101/85e4607d54')
 * // => { source: 'vimeo', id: '1143024101', hash: '85e4607d54' }
 */
export function getVideoDetails(rawUrl) {
  if (!rawUrl) return null;

  let url;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./, '');

  // ---- YouTube ----
  if (host === 'youtu.be' || host.endsWith('youtube.com')) {
    let id =
      url.searchParams.get('v') ||
      (url.pathname.startsWith('/embed/') && url.pathname.split('/')[2]) ||
      (url.pathname.startsWith('/shorts/') && url.pathname.split('/')[2]) ||
      (host === 'youtu.be' && url.pathname.slice(1)) ||
      null;

    if (id) {
      // Clean ID from any query params or hash
      id = id.split(/[?#&]/)[0];
      return { source: 'youtube', id };
    }
    return null;
  }

  // ---- Vimeo ----
  if (host.endsWith('vimeo.com')) {
    const parts = url.pathname.split('/').filter(Boolean); // drop empty segments
    let id = null;
    let hash = null;

    // player.vimeo.com/video/123456?h=abc123
    if (host === 'player.vimeo.com' && parts[0] === 'video' && /^\d+$/.test(parts[1])) {
      id = parts[1];
      hash = url.searchParams.get('h') || null;
    } 
    // vimeo.com/123456 or vimeo.com/123456/abc123 (unlisted)
    else if (/^\d+$/.test(parts[0])) {
      id = parts[0];
      // Check for hash in path (unlisted) or query param
      hash = (parts[1] && /^[A-Za-z0-9]+$/.test(parts[1])) 
        ? parts[1] 
        : (url.searchParams.get('h') || null);
    }

    if (id) {
      return { source: 'vimeo', id, hash };
    }
    return null;
  }

  return null;
}

/**
 * Get video provider from URL
 * @param {string} url - Video URL
 * @returns {'youtube'|'vimeo'|null}
 */
export function getVideoProvider(url) {
  const details = getVideoDetails(url);
  return details ? details.source : null;
}

/**
 * Create a video player based on the URL
 * Automatically detects YouTube or Vimeo and creates the appropriate player
 * 
 * @param {string|HTMLElement} container - Container element or selector
 * @param {string} videoUrl - Video URL (YouTube or Vimeo)
 * @param {object} options - Player options
 * @returns {Promise<object>} Player controls with unified API
 */
export async function createVideoPlayer(container, videoUrl, options = {}) {
  const details = getVideoDetails(videoUrl);
  
  if (!details) {
    throw new Error(`Invalid video URL: ${videoUrl}`);
  }

  if (details.source === 'youtube') {
    const controls = await youtubePlayer.createYouTubePlayer(container, details.id, options);
    // Add provider info and normalize API
    return {
      ...controls,
      provider: 'youtube',
      isPlaying: () => controls.getState() === 1, // YT.PlayerState.PLAYING = 1
    };
  }

  if (details.source === 'vimeo') {
    const vimeoOptions = {
      ...options,
      hash: details.hash,
    };
    return vimeoPlayer.createVimeoPlayer(container, details.id, vimeoOptions);
  }

  throw new Error(`Unsupported video provider for URL: ${videoUrl}`);
}

/**
 * Check if the video API for a provider is ready
 * @param {'youtube'|'vimeo'} provider
 * @returns {boolean}
 */
export function isAPIReady(provider) {
  if (provider === 'youtube') {
    return youtubePlayer.isAPIReady();
  }
  if (provider === 'vimeo') {
    return vimeoPlayer.isAPIReady();
  }
  return false;
}

/**
 * Destroy all video players
 */
export function destroyAllPlayers() {
  youtubePlayer.destroyAllPlayers();
  vimeoPlayer.destroyAllPlayers();
}

// Re-export individual player modules for direct access
export { youtubePlayer, vimeoPlayer };
